import { useState, ReactNode, useEffect } from "react";
import { FormProvider, useFormContext } from "react-hook-form";
import Button from "@/components/Button";
import Stepper from "@/components/Stepper";
import IconButton from "../IconButton";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import Dialog from "@/components/Dialog";
import MobileStepDisplay from "./MobileStepDisplay";
import { LinkProps } from "@generouted/react-router/client";
import { Params, Path } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useScrollToTopOnChange } from "@/hooks/useScrollToTop";
import { ZodSchema } from "zod/v3";
import classNames from "classnames";
import Banner from "../Banner";
import { Action } from "../Banner/shared";
import type { Action as ClusterAction } from "@/components/MenuButton";
import Tooltip from "@/components/Tooltip";

interface StepType {
  headline: string;
  supportingText: string;
}

interface Step extends StepType {
  content: ReactNode;
  requiredFields?: string[];
  /**
   * When set, Next / stepper navigation uses this instead of `form.trigger`.
   * Return false to keep the consultant on the current step.
   */
  validate?: () => boolean | Promise<boolean>;
}

interface MultiStepFormProps {
  form: any;
  steps: Step[];
  onSubmit: (data: any) => void;
  submitDialogContent?: ReactNode;
  submitDialogActions?: (actions: {
    onCancel: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    confirmLabel: string;
  }) => ReactNode;
  /** Called when the submit confirmation dialog closes (overlay, header X, or Cancel). */
  onSubmitDialogClosed?: () => void;
  /** Optional className override for the submit confirmation dialog (e.g. email-preview width). */
  submitDialogClassName?: string;
  /** Forwarded to submit Dialog when it hosts Select or other overflow-prone controls. */
  submitDialogDisplayOverflow?: boolean;
  /** Forwarded to submit Dialog for compact side-sheet layout when it hosts form controls. */
  submitDialogHasFormElements?: boolean;
  renderStepper?: boolean;
  backLink?: LinkProps<Path, Params> | (unknown & { title: string });
  submitText?: string;
  submittingText?: string;
  modelName: string;
  customHeadline?: string | null;
  isEditing?: boolean;
  enableSubmitOnLastStep?: boolean;
  validatePathOnStepClick?: boolean;
  validationSchema?: ZodSchema;
  fullWidth?: boolean;
  objectHeading?: ReactNode;
  /**
   * Persistent content above the current step body (for example a live estimate).
   * Stays mounted across step changes.
   */
  leadingContent?: ReactNode;
  moreActions?: React.ReactNode[];
  /**
   * Extra TopAppBar commands rendered through ActionCluster, plus the submit
   * action. Prefer this over `moreActions` when those commands should overflow
   * like ObjectHeading. Ignored when `submitButtonTooltip` is set, because
   * ActionCluster cannot wrap the submit button in a Tooltip.
   */
  clusterMoreActions?: ClusterAction[];
  disabledSubmit?: boolean;
  /**
   * When true, the header submit stays disabled until the last step, using this
   * form’s `currentStep` (not a parent copy that updates after paint). Submit
   * also re-validates every step and closes a confirm dialog if that fails.
   * Leave false for Job / Event / Facility publish so confirm keeps the prior
   * submit path.
   */
  disableSubmitUntilLastStep?: boolean;
  showActions?: boolean;
  onStepChange?: (step: number) => void;
  isSubmitting?: boolean;
  errorBanner?: string;
  setErrorBanner?: (errorBanner: string | undefined) => void;
  errorPrimaryAction?: Action;
  errorSecondaryAction?: Action;
  submitButtonTooltip?: ReactNode;
  /**
   * When set, Cancel / Discard Changes calls this instead of assigning
   * `window.location` from `backLink`. `backLink.title` still labels the control.
   */
  onCancel?: () => void;
}

interface StepsNavigationProps {
  steps: Step[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  validateStep: (step: number) => Promise<boolean>;
  navigationDisabled?: boolean;
}

function StepsNavigation({
  steps,
  currentStep,
  setCurrentStep,
  validateStep,
  navigationDisabled = false
}: StepsNavigationProps) {
  const form = useFormContext();
  const { errors } = form.formState;
  const [shouldScrollToError, setShouldScrollToError] = useState(false);

  const findFirstInvalidField = () => {
    const currentStepRequiredFields = steps[currentStep].requiredFields;
    if (!currentStepRequiredFields) return null;
    for (const field of currentStepRequiredFields) {
      // Handle nested object paths
      if (field.includes(".")) {
        const fieldParts = field.split(".");
        let currentErrors: any = errors;
        let hasError = false;

        // Traverse the error object using the field parts
        for (const part of fieldParts) {
          if (currentErrors && currentErrors[part]) {
            currentErrors = currentErrors[part];
            hasError = true;
          } else {
            hasError = false;
            break;
          }
        }

        if (hasError) {
          const selectors = [
            `[label="${field}"]`,
            `[name="${field}"]`,
            `[data-field="${field}"] input`,
            `[id="${field}"]`,
            `[aria-label*="${field}"]`,
            `label[for="${field}"] + input`,
            `[data-testid="${field}"] input`,
            `[data-testid="${field}"]`
          ];

          for (const selector of selectors) {
            const element = document.querySelector(selector) as HTMLElement;
            if (element) {
              element.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest"
              });

              if (element.classList.contains("textfield")) {
                const input = element.querySelector("input") as HTMLInputElement;
                if (input) {
                  setTimeout(() => {
                    input.focus();
                  }, 100);
                  return input;
                }
              }

              setTimeout(() => {
                element.focus();
              }, 100);
              return element;
            }
          }
        }
      } else if (errors[field]) {
        // Handle non-nested fields
        const selectors = [
          `[label="${field}"]`,
          `[name="${field}"]`,
          `[data-field="${field}"] input`,
          `[id="${field}"]`,
          `[aria-label*="${field}"]`,
          `label[for="${field}"] + input`,
          `[data-testid="${field}"] input`,
          `[data-testid="${field}"]`
        ];

        for (const selector of selectors) {
          const element = document.querySelector(selector) as HTMLElement;
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest"
            });

            if (element.classList.contains("textfield")) {
              const input = element.querySelector("input") as HTMLInputElement;
              if (input) {
                setTimeout(() => {
                  input.focus();
                }, 100);
                return input;
              }
            }

            setTimeout(() => {
              element.focus();
            }, 100);
            return element;
          }
        }
      }
    }
    return null;
  };

  // Watch for changes in errors and scroll when needed
  useEffect(() => {
    if (shouldScrollToError) {
      const firstInvalidField = findFirstInvalidField();
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
      setShouldScrollToError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, shouldScrollToError]);

  const handleNext = async () => {
    const isStepValid = await validateStep(currentStep);

    if (isStepValid) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      setShouldScrollToError(true);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  return (
    <div className="flex mt-8 h-10">
      {currentStep > 0 && (
        <Button icon="arrow_back_ios" variant="text" onClick={handlePrevious} disabled={navigationDisabled}>
          Previous
        </Button>
      )}
      {currentStep < steps.length - 1 && (
        <Button onClick={handleNext} disabled={navigationDisabled}>
          Next
        </Button>
      )}
    </div>
  );
}

export default function MultiStepForm({
  form,
  steps,
  onSubmit,
  validationSchema,
  renderStepper,
  backLink,
  submitText = "Publish",
  submittingText,
  customHeadline = null,
  submitDialogContent,
  submitDialogActions,
  onSubmitDialogClosed,
  submitDialogClassName,
  submitDialogDisplayOverflow,
  submitDialogHasFormElements,
  modelName,
  isEditing = false,
  enableSubmitOnLastStep = false,
  validatePathOnStepClick = false,
  fullWidth = false,
  objectHeading,
  leadingContent,
  moreActions,
  clusterMoreActions,
  disabledSubmit = false,
  disableSubmitUntilLastStep = false,
  showActions = false,
  onStepChange,
  isSubmitting = false,
  errorBanner,
  setErrorBanner,
  errorPrimaryAction,
  errorSecondaryAction,
  submitButtonTooltip,
  onCancel
}: MultiStepFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const goToStep = (nextStep: number) => {
    setCurrentStep(nextStep);
    onStepChange?.(nextStep);
  };
  const [actions, setActions] = useState<React.ReactNode>(null);
  const [clusterActions, setClusterActions] = useState<ClusterAction[] | undefined>(undefined);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const closeSubmitDialog = () => {
    setShowSubmitDialog(false);
    onSubmitDialogClosed?.();
  };
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
  const { isDirty, isValid: isCurrentStepValid } = form.formState;

  // Use the reusable scroll hook for step changes
  useScrollToTopOnChange([currentStep], {
    shouldScroll: shouldScrollToTop,
    onScrollComplete: () => setShouldScrollToTop(false)
  });

  const validateStep = async (stepIndex: number) => {
    const step = steps[stepIndex];
    if (step?.validate) {
      return await step.validate();
    }

    if (enableSubmitOnLastStep) {
      return await form.trigger();
    }

    return await form.trigger(step?.requiredFields ?? []);
  };

  const validateStepsThrough = async (lastStepIndex: number) => {
    for (let stepIndex = 0; stepIndex <= lastStepIndex; stepIndex++) {
      const isStepValid = await validateStep(stepIndex);
      if (!isStepValid) {
        goToStep(stepIndex);
        setShouldScrollToTop(true);
        return false;
      }
    }
    return true;
  };

  const handleFormValidation = async () => {
    const validationResult = await validationSchema?.safeParseAsync(form.getValues());
    return validationResult?.success;
  };

  const handleShowSubmitDialog = async () => {
    const isValid = (enableSubmitOnLastStep && (await form.trigger())) || (await handleFormValidation());
    if (isValid) {
      setShowSubmitDialog(true);
    }
  };

  const handleSubmit = async () => {
    if (disableSubmitUntilLastStep) {
      const isPathValid = await validateStepsThrough(steps.length - 1);
      if (!isPathValid) {
        closeSubmitDialog();
        return;
      }
    }

    try {
      await onSubmit(form.getValues());
      if (!isSubmitting) {
        closeSubmitDialog();
      }
    } catch (error) {
      // If there's an error, the dialog will stay open and the error will be handled by the parent component
      console.error("Form submission failed:", error);
    }
  };

  const confirmButtonText = customHeadline || `${isEditing ? "Save" : "Publish"} ${modelName}`;
  const submitDisabled =
    disabledSubmit || isSubmitting || (disableSubmitUntilLastStep && currentStep !== steps.length - 1);

  const getActionButton = (onClick: () => void) => {
    const button = (
      <Button
        disabled={submitDisabled}
        variant="filled"
        onClick={onClick}
        loading={isSubmitting}
        loadingLabel={submittingText || undefined}
      >
        {submitText}
      </Button>
    );
    // Only wrap in Tooltip if submitButtonTooltip is provided
    if (submitButtonTooltip) {
      return (
        <Tooltip
          title="Required Fields"
          supportingText={submitButtonTooltip}
          variant="rich"
          color="white"
          positioning="popover"
          anchorCorner="start-start"
          menuCorner="start-start"
          compactHoverArea={true}
        >
          {button}
        </Tooltip>
      );
    }
    return button;
  };

  const isDialogable = !!submitDialogContent || !isEditing;
  const submitOnClick = isDialogable ? handleShowSubmitDialog : handleSubmit;

  const actionButton = getActionButton(submitOnClick);
  const submitClusterAction: ClusterAction = {
    text: submitText,
    variant: "filled",
    disabled: submitDisabled,
    loading: isSubmitting,
    loadingLabel: submittingText || undefined,
    onClick: submitOnClick
  };

  useEffect(() => {
    const updateActions = async () => {
      const isLastStep = currentStep === steps.length - 1;
      const safeExtendedActions = moreActions || [];
      const clusteredExtras = clusterMoreActions != null && !submitButtonTooltip ? clusterMoreActions : undefined;
      const headerNodes = (
        <>
          {safeExtendedActions.map((action) => action)}
          {actionButton}
        </>
      );

      const showHeader = () => {
        if (clusteredExtras) {
          setActions(null);
          setClusterActions([...clusteredExtras, submitClusterAction]);
          return;
        }
        setClusterActions(undefined);
        setActions(headerNodes);
      };

      const hideHeader = () => {
        setActions(null);
        setClusterActions(undefined);
      };

      if (showActions) {
        showHeader();
        return;
      }

      if (isLastStep && enableSubmitOnLastStep) {
        showHeader();
        return;
      }
      if (isDirty && enableSubmitOnLastStep) {
        showHeader();
        return;
      }

      const isFormValid = await handleFormValidation();
      if (isFormValid) {
        showHeader();
      } else {
        hideHeader();
      }
    };

    updateActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditing,
    isCurrentStepValid,
    currentStep,
    submitText,
    submittingText,
    steps.length,
    isDirty,
    moreActions,
    clusterMoreActions,
    submitButtonTooltip,
    isSubmitting,
    disabledSubmit,
    disableSubmitUntilLastStep
  ]);

  useEffect(() => {
    if (steps.length === 0) return;
    if (currentStep > steps.length - 1) {
      goToStep(steps.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length, currentStep]);

  const handleCancel = () => {
    setShowCancelDialog(false);
    if (onCancel) {
      onCancel();
      return;
    }
    if (backLink && "to" in backLink) {
      window.location.href = backLink.to;
    }
  };

  const handleShowCancelDialog = () => {
    if (isSubmitting) return;
    if (isDirty) {
      setShowCancelDialog(true);
    } else {
      handleCancel();
    }
  };

  const cancelButton = (
    <div
      className={classNames("flex items-center", isSubmitting ? "pointer-events-none opacity-40" : "cursor-pointer")}
      onClick={handleShowCancelDialog}
    >
      <IconButton
        name="arrow_back"
        data-testid="back"
        disabled={isSubmitting}
        iconClassName="text-sys-brand-on-surface"
      />
      <span className="text-title-large">{backLink?.title || "Cancel"}</span>
    </div>
  );

  const { isCompactLayout } = useIsCompactLayout();

  function renderFormSteps(isCompactLayout: boolean, steps: Step[], currentStep: number) {
    if (isCompactLayout) {
      return <MobileStepDisplay steps={steps} currentStep={currentStep} handleStepClick={handleStepClick} />;
    }

    return (
      <div className="flex flex-col w-3/12 items-left">
        <Stepper
          variant="status"
          stepsList={steps}
          activeIndex={currentStep}
          onStepClick={handleStepClick}
          className="pl-1.5"
        />
      </div>
    );
  }

  const handleStepClick = async (index: number) => {
    if (isSubmitting) return;

    if (validatePathOnStepClick) {
      if (index <= currentStep) {
        goToStep(index);
        setShouldScrollToTop(true);
        return;
      }

      const isPathValid = await validateStepsThrough(index - 1);

      if (isPathValid) {
        goToStep(index);
        setShouldScrollToTop(true);
      }
      return;
    }

    const isStepValid = await validateStep(currentStep);

    if (isStepValid) {
      goToStep(index);
      setShouldScrollToTop(true);
    }
  };

  return (
    <FocusedPageLayout
      stickyActions={true}
      renderBanner={() => {
        return (
          errorBanner && (
            <Banner
              closable={true}
              description={errorBanner}
              variant="error"
              onClose={() => setErrorBanner?.(undefined)}
              className="sticky z-10 top-[6.7rem] w-full opacity-100"
              primaryAction={errorPrimaryAction}
              secondaryAction={errorSecondaryAction}
            />
          )
        );
      }}
      cancelButton={cancelButton}
      actions={actions}
      clusterActions={clusterActions}
    >
      <div className={classNames("flex flex-col", fullWidth ? "lg:mx-[240px]" : "items-center")}>
        {objectHeading && (
          <div className={classNames("flex w-full justify-start px-4 lg:px-0", !fullWidth && "max-w-[1024px]")}>
            {objectHeading}
          </div>
        )}
        <div
          className={classNames(
            "flex w-full flex-row gap-4 pt-2 lg:pt-16 lg:px-0",
            !fullWidth && "max-w-[1024px]",
            isCompactLayout && "flex-col"
          )}
        >
          {renderStepper && renderFormSteps(isCompactLayout, steps, currentStep)}
          <div className={classNames("flex flex-col", isCompactLayout ? "w-full" : "w-9/12")}>
            <FormProvider {...form}>
              <div className="flex flex-col gap-6">
                {leadingContent}
                {steps[currentStep]?.content}
              </div>
              {steps.length > 1 && (
                <StepsNavigation
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={goToStep}
                  validateStep={validateStep}
                  navigationDisabled={isSubmitting}
                />
              )}
            </FormProvider>
          </div>
        </div>
      </div>

      {showSubmitDialog && (
        <Dialog
          open={showSubmitDialog}
          onClosed={closeSubmitDialog}
          headline={customHeadline || `${isEditing ? "Save" : "Publish"} ${modelName}`}
          className={submitDialogClassName ?? "sm:max-w-[423px] p-6 pr-2"}
          displayOverflow={submitDialogDisplayOverflow}
          hasFormElements={submitDialogHasFormElements}
          actions={
            submitDialogActions?.({
              onCancel: closeSubmitDialog,
              onSubmit: handleSubmit,
              isSubmitting,
              confirmLabel: confirmButtonText
            }) ?? (
              <>
                <Button variant="text" onClick={closeSubmitDialog} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  onClick={handleSubmit}
                  testId="publish-button"
                  loading={isSubmitting}
                  loadingLabel={submittingText || undefined}
                >
                  {confirmButtonText}
                </Button>
              </>
            )
          }
        >
          {submitDialogContent || (
            <p>Are you sure you want to publish this {modelName.toLowerCase()} and make it visible?</p>
          )}
        </Dialog>
      )}

      {showCancelDialog && (
        <Dialog
          open={showCancelDialog}
          onClosed={() => setShowCancelDialog(false)}
          headline={`Discard Changes`}
          className="sm:max-w-[423px] p-6 pr-2"
          actions={
            <>
              <Button variant="text" onClick={() => setShowCancelDialog(false)}>
                Keep Editing
              </Button>
              <Button variant="filled" onClick={handleCancel}>
                Discard Changes
              </Button>
            </>
          }
        >
          <p>Do you want to discard your changes to this {modelName.toLowerCase()}? This cannot be undone.</p>
        </Dialog>
      )}
    </FocusedPageLayout>
  );
}
